import { IOrderDetail } from '@/api/types/order';
import { Currency, User } from '@/api/types/public';
import { env } from '@/utils/env';
import dayjs from 'dayjs';
import { ProviderType, TrackingEvent } from '../types';
import { resolveTrackingEventId } from '../utils/eventIdGenerator';

/**
 * 创建订单成功后的事件适配器
 * @param data
 * @param provider
 * @returns
 */
export function createOrderSuccessAdapter(
  data: {
    totalQuantity: number; // 商品总数量（可供各平台按需使用）
    orderRes: any;
    gtagItems: any;
    currencyName: string;
    grandTotal: number;
    discount: number;
  },
  provider: ProviderType,
  eventId?: string,
): TrackingEvent | TrackingEvent[] | null {
  const { orderRes, gtagItems, currencyName, grandTotal, discount, totalQuantity } = data;
  switch (provider) {
    case ProviderType.TIKTOK: {
      const resolvedTikTokEventId = resolveTrackingEventId(eventId);
      return {
        eventName: 'PlaceAnOrder',
        eventId: resolvedTikTokEventId,
        properties: {
          quantity: totalQuantity,
          content_type: 'product',
          content_ids: gtagItems.map((item) => {
            return item.item_id;
          }),
          currency: currencyName,
          value: grandTotal,
          event_id: resolvedTikTokEventId,
          server_report_data: {
            contents: gtagItems.map((item) => {
              return {
                content_id: item.item_id,
                content_type: 'product',
                content_name: item.item_name,
                price: item.price,
              };
            }),
            currency: currencyName,
            value: grandTotal,
          },
        },
      };
    }

    case ProviderType.GA4:
      return [
        {
          eventName: 'add_payment_info',
          eventId,
          properties: {
            coupon: discount,
            currency: currencyName,
            value: grandTotal,
            payment_type: orderRes.payment_type,
            items: gtagItems,
          },
        },
        {
          eventName: 'add_shipping_info',
          eventId,
          properties: {
            coupon: discount,
            currency: currencyName,
            value: grandTotal,
            shipping_tier: orderRes.freight_type,
            items: gtagItems,
          },
        },
        {
          eventName: 'place_order',
          eventId,
          properties: {
            event_label: 'create_order',
            event_category: 'paymentorders',
            order_on: orderRes.order_no,
          },
        },
      ];
    case ProviderType.FACEBOOK:
      return {
        eventName: 'InitiateCheckout',
        eventId,
      };
    case ProviderType.TWITTER:
      return [
        {
          eventName: 'tw-of32e-of32n',
          properties: {},
        },
        {
          eventName: 'tw-of2hc-of323',
          properties: {},
        },
      ];
    case ProviderType.SNOWPLOW:
      return {
        eventName: 'CreateOrder_Result',
        eventId,
        properties: {
          order_no: orderRes?.order_no,
          currency: currencyName,
          grand_total: grandTotal,
          total_quantity: totalQuantity,
        },
      };
    default:
      return null;
  }
}

// 支付成功页的上报事件适配器
export function purchaseSuccessAdapter(
  data: {
    orderInfo: IOrderDetail;
    shippingAddress: IOrderDetail['order_address'][0];
    userInfo: User;
    currencyList: Currency[];
  },
  provider: ProviderType,
  eventId?: string,
): TrackingEvent | TrackingEvent[] | null {
  const { orderInfo, shippingAddress, userInfo, currencyList } = data;
  const { order_no, order_items, tax_price, freight_type, id } = orderInfo;
  const value = orderInfo.payable.replace(/[^\d.]/g, ''); // 订单总价格
  const currency = currencyList.find((item) => item.name === orderInfo.now_currency);
  let currencyName = '';
  if (!currency) {
    currencyName = orderInfo.currency.name;
  } else {
    currencyName = currency.name;
  }
  if (currencyName === 'US') {
    currencyName = 'USD';
  }

  const gaItems = order_items.map((d) => {
    return {
      id: d.shop_sku,
      name: d.short_name,
      sku: d.shop_sku,
      category: d.prescription.prescription_type.replace(/(?=(?!(^|\b))[A-Z])/g, ' '),
      item_id: d.shop_sku,
      item_name: d.short_name,
      currency: currencyName,
      price: +d.item_total_original_price.replace(/[^\d.]/g, ''),
      item_variant: d.frame_color,
      quantity: d.product_quantity,
    };
  });

  const aleid = localStorage.getItem('zl_axon_aleid');

  switch (provider) {
    case ProviderType.GA4:
      return [
        {
          commandType: 'set',
          eventName: 'user_data',
          properties: {
            email: userInfo.email,
          },
        },
        {
          commandType: 'event',
          eventName: 'purchase',
          properties: {
            transaction_id: order_no,
            affiliation: env.NEXT_PUBLIC_URL,
            value: +value,
            currency: currencyName,
            tax: +tax_price || '',
            shipping: freight_type,
            items: gaItems,
            productId: order_items.map((item) => item.shop_sku).join(','),
            productName: order_items.map((item) => item.short_name).join(','),
            productQuantity: order_items.map((item) => item.product_quantity).join(','),
            productPrice: order_items.map((item) => item.product_payable).join(','),
            productVariant: order_items.map((item) => `${item.frame_color}`).join(','),
            revenue: orderInfo.payable,
            payment: orderInfo.freight_type,
            coupon: orderInfo.code,
            event_category: 'PURCHASE',
            event_label: 'Purchase',
          },
        },
        {
          commandType: 'event',
          eventName: 'purchase',
          properties: {
            send_to: env.NEXT_PUBLIC_GA4_AD_ID1,
            value,
            items: orderInfo.order_items.map((item) => ({
              id: item.product_id,
              item_id: item.shop_sku,
              google_business_vertical: 'retail',
            })),
          },
        },
        {
          commandType: 'event',
          eventName: 'purchase',
          properties: {
            send_to: `${env.NEXT_PUBLIC_GA4_AD_ID2}/${env.NEXT_PUBLIC_GA4_PURCHASE}`,
            value,
            currency: currencyName,
            transaction_id: orderInfo.order_no,
          },
        },
      ];
    case ProviderType.BING:
      return [
        {
          // 这里老代码就是空字符串:
          // window.uetq.push('event', '', { 'revenue_value': value, currency })
          eventName: '',
          eventId,
          properties: {
            revenue_value: value,
            currency: currencyName,
          },
        },
      ];
    case ProviderType.FACEBOOK:
      return {
        commandType: 'track',
        eventName: 'Purchase',
        eventId,
        properties: {
          content_type: 'product',
          content_ids: order_items.map((item) => item.shop_sku),
          value,
          currency: currencyName,
          payment_time: dayjs(orderInfo.payment_time).format('YYYY-MM-DD HH:mm:ss'),
        },
      };
    case ProviderType.CRITEO:
      return {
        eventName: 'trackTransaction',
        eventId,
        properties: {
          id,
          item: order_items.map((item) => ({
            id: item.product_id,
            price: item.item_total_original_price,
            quantity: item.product_quantity,
          })),
        },
      };
    case ProviderType.TIKTOK: {
      const resolvedTikTokEventId = resolveTrackingEventId(eventId);
      return {
        eventName: 'CompletePayment',
        eventId: resolvedTikTokEventId,
        properties: {
          value,
          currency: currencyName,
          contents: gaItems.map(({ item_id, item_name, price, quantity }) => ({
            content_id: item_id,
            content_name: item_name,
            price,
            quantity,
            content_type: 'product',
          })),
          event_id: resolvedTikTokEventId,
        },
      };
    }
    case ProviderType.APPLOVIN:
      return {
        eventName: 'purchase',
        eventId,
        properties: {
          currency: env.NEXT_PUBLIC_DEFAULT_CURRENCY,
          transaction_id: order_no,
          value: parseFloat(orderInfo.base_code_after_total_price),
          shipping: parseFloat(orderInfo.base_freight_price),
          tax: parseFloat(orderInfo.base_tax_price),
          user_id: `${id}&${aleid}`,
          items: order_items.map((item) => ({
            item_id: item.spu,
            item_variant_id: item.shop_sku,
            image_url: item.image_url,
            item_name: item.short_name,
            item_category_id: '524',
            price:
              Math.round(
                (parseFloat((item.base_product_payable || '0').replace(/[^\d.]/g, '')) * 100) /
                  item.product_quantity,
              ) / 100,
            quantity: item.product_quantity,
          })),
        },
      };
    case ProviderType.TWITTER: {
      return [
        {
          eventName: 'tw-of2hc-of8c0',
          eventId,
          properties: {
            value,
            currency: currencyName,
            contents: orderInfo.order_items.map((item) => {
              return {
                content_type: 'Products',
                content_id: item.shop_sku,
                content_name: item.short_name,
                content_price: item.item_total_original_price.replace(/[^\d.]/g, ''),
                num_items: item.product_quantity,
                content_group_id: item.prescription.prescription_type.replace(
                  /(?=(?!(^|\b))[A-Z])/g,
                  ' ',
                ),
              };
            }),
            conversion_id: order_no,
            email_address: userInfo.email,
            phone_number: shippingAddress.telephone,
          },
        },
        {
          eventName: 'tw-of32e-of8ps',
          eventId,
          properties: {
            value,
            currency: currencyName,
            contents: orderInfo.order_items.map((item) => {
              return {
                content_type: 'Products',
                content_id: item.shop_sku,
                content_name: item.short_name,
                content_price: item.item_total_original_price.replace(/[^\d.]/g, ''),
                num_items: item.product_quantity,
                content_group_id: item.prescription.prescription_type.replace(
                  /(?=(?!(^|\b))[A-Z])/g,
                  ' ',
                ),
              };
            }),
            conversion_id: order_no,
            email_address: userInfo.email,
            phone_number: shippingAddress.telephone,
          },
        },
      ];
    }
    case ProviderType.EMARSYS:
      return {
        eventName: 'purchase',
        eventId,
        properties: {
          orderId: id,
          items: order_items.map((item) => ({
            item: item.shop_sku,
            quantity: item.product_quantity,
            price: parseFloat((item.base_product_payable || '0').replace(/[^\d.]/g, '')), // 以美元为基础的售价
          })),
        },
      };
    case ProviderType.SENSORS:
    case ProviderType.SNOWPLOW:
      return {
        eventName: 'PlaceOrderSuccessPage_View',
        eventId,
        properties: {
          order_no: orderInfo?.order_no,
        },
      };
    case ProviderType.SNAPCHAT:
      return {
        eventName: 'PURCHASE',
        eventId,
        properties: {
          price: parseFloat(orderInfo.payable),
          transaction_id: orderInfo.order_no,
          currency: orderInfo.currency.name,
          item_ids: order_items.map((item) => item.shop_sku),
          item_category: 'eyewear',
          number_items: ((quantityList) => {
            let sum = 0;
            for (const num of quantityList) {
              sum += num;
            }
            return sum;
          })(order_items.map((item) => item.product_quantity)),
        },
      };
    case ProviderType.SNAPCHAT_CONVERSION:
      return {
        eventName: 'PURCHASE',
        eventId,
        properties: {
          transaction_id: orderInfo.order_no,
          currency: orderInfo.currency.name, // 货币代码（如 USD, EUR）
          value: parseFloat(orderInfo.payable), // 订单总价（数字类型）
          contents: order_items.map((item) => ({
            id: item.shop_sku, // 商品 SKU
            quantity: item.product_quantity, // 商品数量
            item_price: parseFloat(item.item_total_original_price.replace(/[^\d.]/g, '')), // 单品价格
          })),
        },
      };
    case ProviderType.RTB_HOUSE:
      return {
        eventName: 'conversion',
        eventId,
        properties: {
          conversionClass: 'order',
          conversionSubClass: 'purchase',
          conversionId: orderInfo.order_no,
          offerIds: order_items.map((item) => item.shop_sku),
          conversionValue: orderInfo.payable,
          conversionCurrency: orderInfo.currency.name,
        },
      };
    default:
      return null;
  }
}
